 /**
 * Unified Teleconsultation Service - ALIGNED VERSION
 * Utilise les types partagés et les nouvelles routes API backend
 * Élimine les duplications avec le backend
 */

import { ApiService } from './api-services/ApiService.js';
import { logger } from '../utils/logger.js';
import { teleconsultationSchema, validateData } from '../schemas/validationSchemas.js';
import authService from './authService.js';
import {
  TeleconsultationRequest,
  TeleconsultationResponse,
  TeleconsultationStats,
  DoctorAvailability,
  WaitingRoomPatient,
  VideoCallInitiationResponse,
  TeleconsultationFilters,
  ApiResponse,
  PaginatedResponse,
  WaitingRoomStats,
} from '../types/shared-types';

class UnifiedTeleconsultationService {
  private static instance: UnifiedTeleconsultationService;
  private api: ApiService;

  private constructor() {
    this.api = ApiService.getInstance();
  }

  public static getInstance(): UnifiedTeleconsultationService {
    if (!UnifiedTeleconsultationService.instance) {
      UnifiedTeleconsultationService.instance = new UnifiedTeleconsultationService();
    }
    return UnifiedTeleconsultationService.instance;
  }

  // ===== ALIGNED API METHODS =====

  async createTeleconsultation(data: TeleconsultationRequest): Promise<TeleconsultationResponse> {
    try {
      // ✅ VALIDATION ZOD - Valider les données avant envoi
      const validation = validateData(teleconsultationSchema, data);
      if (!validation.success) {
        logger.error('Validation failed for teleconsultation request:', validation.errors);
        throw new Error(`Données invalides: ${validation.errors.join(', ')}`);
      }
      const validatedData = validation.data;

      // Route backend réelle: POST /api/teleconsult
      // La création se fait via les routes de teleconsult
      const payload = {
        symptoms: validatedData.symptoms,
        urgency: validatedData.urgency,
        notes: validatedData.notes,
        doctorId: validatedData.doctorId,
      };

      const response = await this.api.post<Record<string, unknown>>('/api/teleconsult', payload);

      return (response?.data as TeleconsultationResponse) || (response?.consultation as TeleconsultationResponse) || (response as unknown as TeleconsultationResponse);
    } catch (error) {
      logger.error('Failed to create teleconsultation:', error);
      throw error;
    }
  }

  async getTeleconsultationById(id: number | string): Promise<TeleconsultationResponse> {
    try {
      const response = await this.api.get<
        ApiResponse<TeleconsultationResponse> & { consultation?: TeleconsultationResponse }
      >(`/api/teleconsult/${id}`);

      if (response?.consultation) {
        return response.consultation;
      }

      if (response?.data) {
        return response.data;
      }

      throw new Error('Invalid response format');
    } catch (error) {
      logger.error('Failed to get teleconsultation:', error);
      throw error;
    }
  }

  async getTeleconsultations(
    filters?: TeleconsultationFilters
  ): Promise<TeleconsultationResponse[]> {
    try {
      // Nouvelle route backend: GET /api/teleconsult avec query params
      const queryParams = new URLSearchParams();
      if (filters?.status) queryParams.append('status', filters.status);
      if (filters?.limit) queryParams.append('limit', filters.limit.toString());
      if (filters?.offset) queryParams.append('offset', filters.offset.toString());

      const queryString = queryParams.toString();
      const url = `/api/teleconsult${queryString ? `?${queryString}` : ''}`;

      const response = await this.api.get<
        ApiResponse<TeleconsultationResponse[]> & { consultations?: TeleconsultationResponse[] }
      >(url);

      if (Array.isArray(response?.data)) {
        return response.data;
      }

      if (Array.isArray(response?.consultations)) {
        return response.consultations;
      }

      return [];
    } catch (error) {
      logger.error('Failed to get teleconsultations:', error);
      throw error;
    }
  }

  async updateTeleconsultation(
    id: number | string,
    data: Partial<{
      diagnosis?: string;
      prescription?: string;
      recommendations?: string;
      status?: TeleconsultationResponse['status'];
      results?: string;
    }>
  ): Promise<TeleconsultationResponse> {
    try {
      // Utiliser les vraies routes backend selon l'opération
      if (data.status === 'CANCELLED') {
        await this.api.post<ApiResponse<void>>(`/api/teleconsult/${id}/reject`, {
          reason: data.results || data.recommendations || 'Annulation depuis client',
        });
        return this.getTeleconsultationById(id);
      }

      if (
        data.status === 'COMPLETED' ||
        data.diagnosis ||
        data.prescription ||
        data.recommendations
      ) {
        const response = await this.api.post<ApiResponse<TeleconsultationResponse>>(
          `/api/teleconsult/${id}/complete`,
          {
            diagnosis: data.diagnosis,
            prescription: data.prescription,
            notes: data.recommendations,
          }
        );

        if (response?.data) {
          return response.data;
        }
        return this.getTeleconsultationById(id);
      }

      throw new Error('Operation not supported by backend route set');
    } catch (error) {
      logger.error('Failed to update teleconsultation:', error);
      throw error;
    }
  }

  async cancelTeleconsultation(id: number | string): Promise<void> {
    try {
      // Backend route: POST /api/teleconsult/:id/reject
      await this.api.post<ApiResponse<void>>(`/api/teleconsult/${id}/reject`, {});
    } catch (error) {
      logger.error('Failed to cancel teleconsultation:', error);
      throw error;
    }
  }

  async getDoctorAvailability(): Promise<DoctorAvailability[]> {
    try {
      const response = await this.api.get<
        ApiResponse<DoctorAvailability[]> & { doctors?: DoctorAvailability[] }
      >('/api/teleconsult/waiting-room/doctor-availability');

      if (Array.isArray(response?.data)) {
        return response.data;
      }

      if (Array.isArray(response?.doctors)) {
        return response.doctors;
      }

      return [];
    } catch (error) {
      logger.error('Failed to get doctor availability:', error);
      throw error;
    }
  }

  async startVideoCall(teleconsultationId: number): Promise<VideoCallInitiationResponse> {
    try {
      // Démarrer la consultation en récupérant les tokens réels
      const response = await this.api.post<Record<string, unknown>>(
        `/api/teleconsult/${teleconsultationId}/accept`,
        {}
      );

      const result = (response?.data as VideoCallInitiationResponse) || (response as unknown as VideoCallInitiationResponse);
      if (result) {
        return result;
      }

      throw new Error('Failed to start video call - no tokens received');
    } catch (error) {
      logger.error('Failed to start video call:', error);
      throw error;
    }
  }

  async endVideoCall(teleconsultationId: number): Promise<VideoCallInitiationResponse> {
    try {
      // Terminer la consultation
      const response = await this.api.post<ApiResponse<VideoCallInitiationResponse>>(
        `/api/teleconsult/${teleconsultationId}/complete`,
        { notes: 'Video call ended' }
      );

      if (response?.data) {
        return {
          channelName: `consultation-${teleconsultationId}`,
          token: '',
          uid: 12345,
          status: 'COMPLETED',
        };
      }

      throw new Error('Failed to end video call');
    } catch (error) {
      logger.error('Failed to end video call:', error);
      throw error;
    }
  }

  async cancelVideoCall(
    teleconsultationId: number,
    reason?: string
  ): Promise<VideoCallInitiationResponse> {
    try {
      // Annuler la consultation
      await this.api.post<ApiResponse<void>>(`/api/teleconsult/${teleconsultationId}/reject`, {
        reason: reason || 'Video call cancelled',
      });

      return {
        channelName: `consultation-${teleconsultationId}`,
        token: '',
        uid: 12345,
        status: 'CANCELLED',
      };
    } catch (error) {
      logger.error('Failed to cancel video call:', error);
      throw error;
    }
  }

  async getConsultationHistory(
    page: number = 1,
    limit: number = 20
  ): Promise<PaginatedResponse<TeleconsultationResponse>> {
    try {
      // Utiliser la route existante GET /api/teleconsult/history avec filtrage par statut COMPLETED
      const queryParams = new URLSearchParams();
      queryParams.append('status', 'COMPLETED');
      queryParams.append('limit', limit.toString());
      queryParams.append('offset', ((page - 1) * limit).toString());

      const queryString = queryParams.toString();
      const url = `/api/teleconsult/history${queryString ? `?${queryString}` : ''}`;

      const response = await this.api.get<ApiResponse<TeleconsultationResponse[]>>(url);

      if (response?.data) {
        // Transformer le tableau en format paginé
        const data = response.data;
        return {
          data,
          total: data.length, // TODO: Implémenter une vraie pagination backend
          page,
          totalPages: Math.ceil(data.length / limit),
          hasNext: page * limit < data.length,
          hasPrev: page > 1,
        };
      }

      return { data: [], total: 0, page, totalPages: 0, hasNext: false, hasPrev: false };
    } catch (error) {
      logger.error('Failed to get consultation history:', error);
      throw error;
    }
  }

  async getStatistics(_filters?: {
    startDate?: string;
    endDate?: string;
  }): Promise<TeleconsultationStats> {
    try {
      // Route backend unifiée: GET /api/consultations/stats/summary
      const response = await this.api.get<ApiResponse<TeleconsultationStats>>(
        '/api/consultations/stats/summary'
      );

      if (response?.data) {
        return response.data;
      }

      throw new Error('Invalid response format');
    } catch (error) {
      logger.error('Failed to get statistics:', error);
      // Fallback aux statistiques simulées si la route n'existe pas encore
      return {
        totalConsultations: 0,
        completedConsultations: 0,
        pendingConsultations: 0,
        activeConsultations: 0,
        averageConsultationTime: 25,
        patientSatisfaction: 4.2,
        successRate: 0,
      };
    }
  }

  // ===== LEGACY METHODS FOR BACKWARD COMPATIBILITY =====
  // These will be removed in future versions

  async getWaitingRoom(_doctorId?: number): Promise<WaitingRoomPatient[]> {
    const result = await this.getWaitingRoomPatients();
    return result.data;
  }

  async endTeleconsultation(
    id: number | string,
    data?: { diagnosis?: string; prescription?: string; recommendations?: string }
  ): Promise<TeleconsultationResponse> {
    try {
      // Utiliser la vraie route backend
      const response = await this.api.post<ApiResponse<TeleconsultationResponse>>(
        `/api/teleconsult/${id}/complete`,
        {
          diagnosis: data?.diagnosis,
          prescription: data?.prescription,
          notes: data?.recommendations,
        }
      );

      if (response?.data) {
        return response.data;
      }

      throw new Error('Invalid response format');
    } catch (error) {
      logger.error('Failed to end teleconsultation:', error);
      throw error;
    }
  }

  async acceptNextPatient(teleconsultationId?: string | number): Promise<TeleconsultationResponse> {
    try {
      const idStr = teleconsultationId !== undefined && teleconsultationId !== null ? String(teleconsultationId).trim() : '';
      const isIdValid = idStr !== '' && idStr.toLowerCase() !== 'nan';
      
      const endpoint = isIdValid 
        ? `/api/teleconsult/${idStr}/accept`
        : '/api/teleconsult/waiting-room/call-next';
      
      const response = await this.api.post<ApiResponse<TeleconsultationResponse>>(
        endpoint,
        {}
      );

      if (response?.data) {
        return response.data;
      }

      throw new Error('Invalid response format');
    } catch (error) {
      logger.error('Failed to accept next patient:', error);
      throw error;
    }
  }

  // Méthode pour rejoindre une salle de consultation vidéo (utilisée par le hook Agora)
  async joinConsultationRoom(consultationId: number | string): Promise<{ roomId: string; token: string; uid: number | string; appId?: string }> {
    try {
      // Récupérer les données de la consultation pour obtenir les vraies infos vidéo
      const consultation = await this.getTeleconsultationById(consultationId);

      const channelName = consultation.agora_channel_name;
      const c = (consultation as unknown) as Record<string, unknown>;
      const fallbackToken = c.agora_token as string | undefined;
      const doctorToken = c.doctor_agora_token as string | undefined;
      const patientToken = c.patient_agora_token as string | undefined;
      let currentUserId: string | number | undefined;
      let currentUserRole: string | undefined;
      try {
        const currentUser = authService.getUser();
        if (currentUser) {
          currentUserId = currentUser.id;
          currentUserRole = currentUser.role;
        }
      } catch (error) {
        logger.error('Failed to get current user from authService for token assignment', { error });
      }

      const pId = c.patientId || c.patient_id;
      const dId = c.doctorId || c.doctor_id;
      const rId = c.requester_id || c.requesterId;

      // Déduction intelligente de l'utilisateur si authService ne renvoie pas d'infos
      if (!currentUserId && pId && dId) {
        // En mode patient, on est probablement le patient
        if (window.location.pathname.includes('/patient/')) {
          currentUserId = pId as string;
          currentUserRole = 'PATIENT';
          logger.info('[RTC DEBUG] currentUserId inferred from URL/Consultation as PATIENT:', currentUserId);
        } else if (window.location.pathname.includes('/doctor/')) {
          currentUserId = dId as string;
          currentUserRole = 'DOCTOR';
          logger.info('[RTC DEBUG] currentUserId inferred from URL/Consultation as DOCTOR:', currentUserId);
        }
      }

      const isDoctor = currentUserRole === 'DOCTOR' || (currentUserId && String(currentUserId) === String(dId));
      const isPatient = currentUserRole === 'PATIENT' || (currentUserId && String(currentUserId) === String(pId));
      const isRequester = currentUserId && String(currentUserId) === String(rId);

      // Récupération sécurisée des tokens avec fallbacks
      const pToken = (c.patient_agora_token as string | undefined) || (c.patientAgoraToken as string | undefined) || patientToken;
      const dToken = (c.doctor_agora_token as string | undefined) || (c.doctorAgoraToken as string | undefined) || doctorToken;
      const rToken = (c.requester_token as string | undefined) || (c.requesterToken as string | undefined);

      // DEBUG LOGS (très verbeux pour identifier le 4096)
      const debugInfo = {
        currentUserId,
        currentUserRole,
        pId,
        dId,
        isPatient,
        isDoctor,
        hasPToken: !!pToken,
        hasDToken: !!dToken,
        channel: channelName
      };
      logger.debug('[RTC DEBUG] Session Join Data:', debugInfo);

      let resolvedToken = consultation.agora_token || fallbackToken;
      
      if (isDoctor) {
        resolvedToken = dToken || resolvedToken;
        logger.debug('[RTC DEBUG] Role: DOCTOR -> Using Doctor Token');
      } else if (isPatient) {
        resolvedToken = pToken || resolvedToken;
        logger.debug('[RTC DEBUG] Role: PATIENT -> Using Patient Token');
      } else if (isRequester) {
        resolvedToken = rToken || dToken || resolvedToken;
        logger.debug('[RTC DEBUG] Role: REQUESTER -> Using Requester Token');
      }

      if (!channelName || !resolvedToken) {
        throw new Error(`Données vidéo incomplètes (Canal: ${!!channelName}, Token: ${!!resolvedToken})`);
      }

      // Retourner les vraies données de la consultation avec l'UID correspondant au token et l'appId
      return {
        roomId: channelName,
        token: resolvedToken,
        uid: currentUserId || 0,
        appId: (c.agora_app_id as string | undefined) || (c.appId as string | undefined),
      };
    } catch (error) {
      logger.error('Failed to join consultation room:', error);
      throw error;
    }
  }

  // Obtenir les statistiques de la salle d'attente
  async getWaitingRoomStats(): Promise<WaitingRoomStats> {
    try {
      const response = await this.api.get<ApiResponse<WaitingRoomStats>>(
        '/api/teleconsult/waiting-room/stats'
      );

      if (response?.data) {
        return response.data;
      }

      return {
        totalWaiting: 0,
        urgentCases: 0,
        averageWaitTime: 0,
        doctorsAvailable: 0,
      };
    } catch (error) {
      logger.error('Failed to get waiting room stats:', error);
      // Return default empty stats
      return {
        totalWaiting: 0,
        urgentCases: 0,
        averageWaitTime: 0,
        doctorsAvailable: 0,
      };
    }
  }

  // Obtenir la liste des patients en salle d'attente
  async getWaitingRoomPatients(): Promise<{ data: WaitingRoomPatient[]; count: number }> {
    try {
      // ✅ Sécurisation : Ne pas appeler cette route si l'utilisateur n'est pas médecin
      let userRole = null;
      try {
        const userStr = localStorage.getItem('user') || localStorage.getItem('currentUser');
        if (userStr) {
          userRole = JSON.parse(userStr).role;
        }
      } catch (e) {
        logger.error('[UnifiedTeleconsultationService] Failed to parse user string from localStorage', e);
      }
      
      const isDoctor = userRole === 'DOCTOR' || userRole === 'ADMIN';
      
      if (!isDoctor) {
        logger.warn('[UnifiedTeleconsultationService] Ignored waiting room patients request (role restricted)');
        return { data: [], count: 0 };
      }

      const response = await this.api.get<Record<string, unknown>>('/api/teleconsult/waiting-room/patients');

      // Le backend renvoie { status: 'success', data: { patients: [...], totalWaiting: X } }
      // api.get renvoie le payload complet
      const rawData = (response?.data as Record<string, unknown>) || response;

      if (rawData?.patients && Array.isArray(rawData.patients)) {
        return {
          data: rawData.patients as WaitingRoomPatient[],
          count: (rawData.totalWaiting as number) || (rawData.patients as unknown[]).length,
        };
      } else if (Array.isArray(rawData)) {
        return {
          data: rawData as WaitingRoomPatient[],
          count: (response.count as number) || rawData.length,
        };
      }

      throw new Error('Invalid response format');
    } catch (error) {
      logger.error('Failed to get waiting room patients:', error);
      throw error;
    }
  }

  // Définir la disponibilité du médecin
  async setAvailability(
    isAvailable: boolean,
    availabilityStatus?: 'AVAILABLE' | 'BUSY' | 'OFFLINE'
  ): Promise<void> {
    try {
      const response = await this.api.post<ApiResponse<void>>('/api/users/set-availability', {
        isAvailable,
        availabilityStatus,
      });

      if (response?.status !== 'success') {
        throw new Error('Failed to set availability');
      }
    } catch (error) {
      logger.error('Failed to set availability:', error);
      throw error;
    }
  }

  // ✅ NOUVEAU: Mise à jour progressive des données cliniques (Autosave)
  async updateClinicalNotes(
    id: number | string,
    data: { notes?: string; diagnosis?: string; recommendations?: string }
  ): Promise<TeleconsultationResponse> {
    try {
      const response = await this.api.patch<ApiResponse<TeleconsultationResponse>>(
        `/api/teleconsult/${id}/notes`,
        data
      );

      if (response?.data) {
        return response.data;
      }

      throw new Error('Invalid response format for updateClinicalNotes');
    } catch (error) {
      logger.error('Failed to update clinical notes:', error);
      throw error;
    }
  }
}

// Export singleton
const unifiedTeleconsultationService = UnifiedTeleconsultationService.getInstance();

// Exports for compatibility
export {
  type TeleconsultationResponse,
  type TeleconsultationRequest,
  type TeleconsultationStats,
  type WaitingRoomPatient,
  unifiedTeleconsultationService as teleconsultService,
  unifiedTeleconsultationService as consulationService,
};

export default unifiedTeleconsultationService;
